import { isRouteErrorResponse, Link, useRouteError } from 'react-router-dom';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { isChunkLoadError } from '@/app/lazyRoute';

// -----------------------------------------------------------------------------
// The error element for every screen inside the shell.
//
// Without one, react-router replaces the WHOLE app - sidebar included - with
// its developer "Unexpected Application Error!" page. Mounted on a pathless
// route under `AppLayout`, so a broken screen renders here in the content
// column and the rest of the panel stays usable.
// -----------------------------------------------------------------------------

export function RouteError(): JSX.Element {
  const error = useRouteError();
  const stale = isChunkLoadError(error);

  const title = stale ? 'This screen could not be loaded' : 'This screen hit an error';
  const description = stale
    ? 'The panel was updated while this tab was open. Reload to pick up the latest version.'
    : isRouteErrorResponse(error)
      ? `${error.status} ${error.statusText}`
      : 'Something went wrong while rendering this page. Reloading usually clears it; if not, report it with the details below.';
  const detail = !stale && error instanceof Error ? error.message : null;

  return (
    <Card>
      <div role="alert" className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
        <div className="mb-1 flex size-10 items-center justify-center rounded-lg bg-danger-soft text-destructive">
          <AlertCircle className="size-5" aria-hidden />
        </div>
        <p className="text-md font-semibold text-foreground">{title}</p>
        <p className="max-w-md text-base text-muted-foreground">{description}</p>
        {detail ? (
          <code className="mt-1 max-w-xl break-words rounded-md bg-muted px-2 py-1 text-sm text-muted-foreground">
            {detail}
          </code>
        ) : null}
        <div className="mt-3 flex flex-wrap justify-center gap-2">
          <Button size="sm" onClick={() => window.location.reload()}>
            <RefreshCw aria-hidden />
            Reload
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/">Back to the dashboard</Link>
          </Button>
        </div>
      </div>
    </Card>
  );
}
